// @flow

import * as React from 'react';

import { UserRound } from 'lucide-react';

import { connect } from 'react-redux';

import { selectCartUUID, CategoryBadge } from '@gandi/cart';
import { CountryName, SubdivisionName, useAddress } from '@gandi/react-places';
import type { Organization } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';
import { Badge, Base, Button } from '@gandi/ui';

import { useModalContext } from 'src/Layout/context/Modal';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { selectShopCurrent } from 'src/modules/organization/selectors';
import type { PreFetchArgs, FullState, PreFetchReturn } from 'src/types';

import styles from './InvoiceTo.scss';

// INFO: We decided to not display the drawer for invoice organization for now,
// but we want to keep the code in case we want to enable it later
const ENABLE_DRAWER_FOR_INVOICED_ORGA = false;

type AddressProps = {
  org: Organization,
};

function Address({ org }: AddressProps): React.Node {
  const { address } = useAddress({
    data: org,
    showRecipient: true,
  });

  return <article className={styles.address}>{address}</article>;
}

type StateProps = {
  org: ?Organization,
  configurationLink: string,
};

type Props = {
  ...StateProps,
};

function InvoiceTo({ org, configurationLink }: Props): null | React.Node {
  const { __ } = useTranslator();

  const displayNewAccountChoice = useFeatureFlag('FF_SHOP_ACCOUNT_CHOICE');
  const context = useModalContext();

  // User is not logged-in. The invoice recipient hasn't been set yet.
  if (!org) {
    return null;
  }

  function handleChangeCartOwner(evt: SyntheticEvent<HTMLButtonElement>) {
    evt.preventDefault();
    context.openModal({ type: 'invoiceSelectModal', replace: true });
  }

  const displayName = org.name;

  const extraProps = displayNewAccountChoice
    ? {
        onClick: ENABLE_DRAWER_FOR_INVOICED_ORGA ? handleChangeCartOwner : null,
        type: 'button',
        flexJustifyStart: true,
      }
    : {};

  return (
    <Base backgroundColor="surface" flex flexCol className={styles.root}>
      <CategoryBadge>{__('Invoice to')}</CategoryBadge>

      <div>
        <div className={styles.iconBlock}>
          <UserRound />
        </div>
        <Badge className={styles.badge} intent="muted">
          {org.type_label}
        </Badge>
        <div className={styles.name}>{displayName}</div>
      </div>

      <Address org={org} />

      <Button kind="link" to={configurationLink} {...extraProps}>
        {__('Change')}
      </Button>
    </Base>
  );
}

InvoiceTo.preFetch = function preFetch(
  args: PreFetchArgs<{}, void, empty, FullState>,
): PreFetchReturn<> {
  const { dispatch, getState } = args;
  const org = selectShopCurrent(args.getState(), true);
  const country = org?.country;

  const promises = [CountryName.preFetch({ dispatch, getState })];

  if (country) {
    promises.push(
      SubdivisionName.preFetch({
        dispatch,
        getState,
        country,
        // FIXME: subdivision is useless in SubdivisionName
        subdivision: '',
      }),
    );
  }

  return Promise.allSettled(promises);
};

const mapStateToProps = (state: FullState): Partial<Props> => {
  const cartId = selectCartUUID(state);
  const configurationLink = `/cart/${cartId}/configure/account-choice`;
  const org = selectShopCurrent(state, true);

  return {
    org,
    configurationLink,
  };
};

// $FlowFixMe[signature-verification-failure]
// $FlowFixMe[underconstrained-implicit-instantiation]
export default connect(mapStateToProps)(InvoiceTo);
